import {Language} from '../constants/language.constants';

export function isElementInViewport(el: HTMLElement): boolean {
  const rect = el.getBoundingClientRect();

  return (
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <=
      (window.innerHeight ||
        document.documentElement.clientHeight) /* or $(window).height() */ &&
    rect.left <=
      (window.innerWidth ||
        document.documentElement.clientWidth) /* or $(window).width() */
  );
}

export function getDefaultLanguage(): Language {
  const language = navigator.language.split('-').shift() ?? 'nl';
  const languageIndex = Object.values(Language).indexOf(language as Language);
  const key = Object.keys(Language)[languageIndex];
  return languageIndex > -1
    ? Language[key as keyof typeof Language]
    : Language.NL;
}

export function downloadFile(textInput: string, filename: string): void {
  const link = document.createElement('a');
  link.setAttribute(
    'href',
    'data:text/vcard;charset=utf-8, ' + encodeURIComponent(textInput),
  );
  link.setAttribute('download', filename);
  link.click();
}

export const toDataUrl = (
  src: string,
  outputFormat = 'image/jpeg',
): Promise<string> => {
  return new Promise(resolve => {
    const image = new Image();
    image.crossOrigin = 'Anonymous';
    image.onload = function () {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.height = image.naturalHeight;
      canvas.width = image.naturalWidth;
      ctx?.drawImage(image, 0, 0);
      const dataURL = canvas.toDataURL(outputFormat);
      resolve(dataURL.replace(/^data:image\/(png|jpg|jpeg);base64,/, ''));
    };
    image.src = src;
    if (image.complete || image.complete === undefined) {
      image.src =
        'data:image/gif;base64, R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';
      image.src = src;
    }
  });
};

export const formatAverageRating = (rateAverage?: number | null) => {
  if (rateAverage === undefined || rateAverage === null) {
    return '?';
  }
  if (rateAverage % 1) {
    return (Math.round(rateAverage * 10) / 10).toFixed(1);
  }
  return rateAverage;
};

// Localized date formatting helpers for month-year ranges

export function formatMonthYear(
  dateInput: string | null | undefined,
  language: Language,
): string | undefined {
  if (!dateInput) {
    return undefined;
  }

  const parsed = new Date(dateInput);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }

  const locale = language === Language.NL ? 'nl-NL' : 'en-US';
  const formatted = new Intl.DateTimeFormat(locale, {
    month: 'short',
    year: 'numeric',
  }).format(parsed);

  if (language === Language.NL) {
    // Normalize Dutch short month by removing trailing dots and using lowercase
    return formatted.replaceAll('.', '').toLowerCase();
  }

  return formatted;
}

export function formatDateRange(
  startDateInput: string | null | undefined,
  endDateInput: string | null | undefined,
  language: Language,
): string {
  const start = formatMonthYear(startDateInput, language);
  const end = formatMonthYear(endDateInput, language);

  const nowLabel = language === Language.NL ? 'heden' : 'present';

  if (start && (end || !endDateInput)) {
    return `${start} - ${end ?? nowLabel}`;
  }

  if (start) {
    return start;
  }

  return end ?? '';
}
